import _ from 'underscore';
import Promise from 'bluebird';
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
// import { validateImageSizeAsync } from '@bingads-webui-campaign/image-experience-utils';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';

import {
  handleRealUsedFlag,
  RealUsedFlagSuffix,
  MaxAddAssetJobInParell,
  getImageAssetLimitPerAccount,
  AIModelSource,
} from '@bingads-webui-campaign-react/asset-library-model';
import { AssetLibraryAIGenerateImagesView, useAIGenerateImagesState, AIGCDisclaimer } from '@bingads-webui-campaign-react/asset-library-ai-generate-images-view';

const MAX_FOLDER_FETCH_LIMIT = 20;
const singularImageString = _TL_('{{selectedNumber}} of {{limitNumber}} image selected');
const pluralImagesString = _TL_('{{selectedNumber}} of {{limitNumber}} images selected');
const errorExceededMaxCount = _TL_('You\'ve exceeded the max. number of assets you can add at a time ({{limitNumber}}). Please try again.');

const ManagedAIGCImageView = (props) => {
  const {
    assetLibraryConfig, assetLibraryDataService, triggerAddImagesRef, setShowBlocker,
    setShowApplyButton, setDisableApplyButton,
    maxUploadNum, setSelectionInfoText, setErrors,
    i18n, perfMarker, logger, aid, permissions, eventHandler,
  } = props;
  const { assetLibraryAccountPreferences } = assetLibraryConfig || {};

  const { selectedImages, prompt, ...restState } = useAIGenerateImagesState({
    dataService: assetLibraryDataService, i18n, logger, eventHandler,
  });

  // Only supports maximum of adding 20 images at a time in a folder. Please contact MT to raise limit on API first
  const boundedMaxUploadNum = maxUploadNum > MAX_FOLDER_FETCH_LIMIT ? MAX_FOLDER_FETCH_LIMIT : maxUploadNum;

  useEffect(() => {
    const selectedCount = (selectedImages && selectedImages.length) || 0;
    if (selectedCount > boundedMaxUploadNum) {
      setErrors([i18n.getString(errorExceededMaxCount, { limitNumber: boundedMaxUploadNum })]);
      setDisableApplyButton(true);
      setSelectionInfoText('');
    } else {
      setErrors([]);
      setDisableApplyButton(false);
      setShowApplyButton(selectedCount > 0);
      const stringToUse = boundedMaxUploadNum > 1 ? pluralImagesString : singularImageString;
      setSelectionInfoText(i18n.getString(stringToUse, { selectedNumber: selectedCount, limitNumber: boundedMaxUploadNum }));
    }
  }, [boundedMaxUploadNum, i18n, selectedImages, setDisableApplyButton, setErrors, setSelectionInfoText, setShowApplyButton]);

  const handleJobErrors = useCallback((jobErrors) => {
    if (jobErrors.length > 0) {
      const assetLimitError = _.find(jobErrors, (err) => {
        const errorCode = _.get(err, ['body', 'value', 0, 'Code'], '');
        return errorCode === 'ImagesLimitExceededPerAccount';
      });

      if (assetLimitError) {
        const assetLimit = getImageAssetLimitPerAccount(aid, logger);
        setErrors([i18n.getString(_TL_('One or more images failed to upload because your account has exceeded the {{value}}-image asset limit. Please remove assets and try again.'), { value: assetLimit })]);
      } else {
        const genericError = (
          <span>
            { i18n.getString(_TL_('One or more assets failed to upload. Please try again')) }
            <LearnMoreLink linkText={i18n.getString(_TL_('Learn more about asset guidelines and accepted file types.'))} topic="app54823" />
          </span>
        );
        setErrors([genericError]);
      }
    }
  }, [aid, i18n, logger, setErrors]);

  const onApplySelectedImages = useCallback(() => {
    // no selected images, or selected too many (shouldn't happen)
    if (_.isEmpty(selectedImages) || selectedImages.length > boundedMaxUploadNum) {
      return Promise.resolve();
    }

    handleRealUsedFlag(assetLibraryAccountPreferences, RealUsedFlagSuffix.Picker);
    logger.logTrace('Selected AI generated images from asset library in ad creation flow', { length: selectedImages.length });
    logger.logTrace('Count selected AI images', {
      imageUrls: selectedImages.map(x => x.thumbnailSrc).join(','),
      count: selectedImages.length,
      logForExpand: true,
      prompt,
    }); // will expand the log to per image id

    setShowBlocker(true);

    const aiGCImagePerfMarker = perfMarker.createChild('AIGCImagePicker');
    aiGCImagePerfMarker.willInit();

    const jobErrors = [];
    return Promise.map(
      selectedImages,
      (img) => {
        const { thumbnailSrc: imageUrl, modelSource } = img;

        logger.logTrace('AIGC image save', {
          imageUrl,
          prompt,
          modelSource: modelSource || AIModelSource.Dalle,
        });

        return assetLibraryDataService.uploadImageByUrl({
          imageUrl, sourceType: modelSource === AIModelSource.Recraft ? constants.sourceTypes.AIGeneratedByRecraft : constants.sourceTypes.AIGenerated,
        }).catch((err) => {
          jobErrors.push(err);
        });
      },
      { concurrency: MaxAddAssetJobInParell }
    ).then((values) => {
      const imageIds = _.compact(values || []);
      if (!_.isEmpty(imageIds)) {
        logger.logTrace('Saved AI generated images to folder in ad creation flow');
        // use the tag "logForExpand" to expand the log to per image id without expanding other logs with imageIds
        logger.logTrace('Count AI saved images', {
          imageIds: imageIds.join(','),
          count: imageIds.length,
          logForExpand: true,
          prompt,
        }); // will expand the log to per image id
        if (eventHandler && eventHandler.onAddImagesToAssetLibrary) {
          eventHandler.onAddImagesToAssetLibrary({ imageIds, prompt });
        }
        return assetLibraryConfig.onImagesSelected(imageIds, constants.imageSources.AIGenerated);
      }
      return Promise.resolve();
    }).finally(() => {
      setShowBlocker(false);
      handleJobErrors(jobErrors);
      aiGCImagePerfMarker.done();
    });
  }, [assetLibraryAccountPreferences, assetLibraryConfig, assetLibraryDataService, boundedMaxUploadNum, handleJobErrors, logger, perfMarker, selectedImages, setShowBlocker, prompt, eventHandler]);

  triggerAddImagesRef.current = onApplySelectedImages;

  return (
    <AssetLibraryAIGenerateImagesView
      i18n={i18n}
      prompt={prompt}
      logger={logger}
      {...restState}
      assetLibraryAccountPreferences={assetLibraryAccountPreferences}
      permissions={permissions}
      eventHandler={eventHandler}
    />
  );
};

ManagedAIGCImageView.propTypes = {
  assetLibraryConfig: PropTypes.shape({
    onImagesSelected: PropTypes.func,
  }).isRequired,
  assetLibraryDataService: PropTypes.shape({
    uploadImageByUrl: PropTypes.func,
  }).isRequired,
  triggerAddImagesRef: PropTypes.shape({}).isRequired,
  setShowBlocker: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
  setShowApplyButton: PropTypes.func,
  setDisableApplyButton: PropTypes.func,
  setSelectionInfoText: PropTypes.func,
  setErrors: PropTypes.func,
  maxUploadNum: PropTypes.number,
  aid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  permissions: PropTypes.shape({}),
  eventHandler: PropTypes.shape({
    onAddImagesToAssetLibrary: PropTypes.func,
  }),
};

ManagedAIGCImageView.defaultProps = {
  setShowBlocker: _.noop,
  setShowApplyButton: _.noop,
  setDisableApplyButton: _.noop,
  setSelectionInfoText: _.noop,
  setErrors: _.noop,
  maxUploadNum: MAX_FOLDER_FETCH_LIMIT,
  permissions: {},
  eventHandler: null,
};

export const aigcImageSourceConfig = {
  title: _TL_('Generate images'),
  showBackButton: true,
  onRenderContent: props => <ManagedAIGCImageView {...props} />,
  onRenderFooterContent: props => <AIGCDisclaimer i18n={props.i18n} />,

  // don't show the button, instead show a Generate images button in the banner
  // buttonConfig: {
  //   label: _TL_('Generate images'),
  //   view: VIEWS.AIGCImage,
  //   iconName: CustomFluentUIIcons.SparkeSVGIcon,
  //   iconStyles: {
  //     root: {
  //       position: 'relative',
  //       top: -3,
  //       left: 1,
  //     },
  //   },
  //   buttonLocator: 'aigc-images',
  // },
};
